// ─────────────────────────────────────────────────────────────────────────────
// read_chart_data – Pull a specific registered widget's full data on demand.
//
// The system prompt only ships the widget *index* (id, title, kind, period,
// units). When Claude needs the actual numbers, it calls this tool with the
// widgetId. This keeps the system prompt small and lets the conversation
// scale to dashboards with many widgets.
// ─────────────────────────────────────────────────────────────────────────────

import type { RegisteredWidget } from "../registry/types";
import { widgetRegistry } from "../registry/widgetRegistry";

export interface ReadChartDataInput {
	chart_id?: string;
	chart_title?: string;
	include?: Array<"series" | "table" | "drilldown" | "filters" | "source">;
	drilldown?: Record<string, string>;
	max_rows?: number;
	max_series_points?: number;
}

export interface ReadChartDataOutput {
	found: boolean;
	widget?: Partial<RegisteredWidget> & { widgetId: string; title: string };
	resolved_drilldown?: RegisteredWidget["series"];
	warning?: "ambiguous_title" | "stale" | "truncated" | "not_found";
	message?: string;
}

const DEFAULT_MAX_ROWS = 200;
const DEFAULT_MAX_SERIES_POINTS = 200;

function fuzzyTitleMatch(title: string, all: RegisteredWidget[]): RegisteredWidget[] {
	const t = title.trim().toLowerCase();
	if (!t) return [];
	const exact = all.filter((w) => w.title.toLowerCase() === t);
	if (exact.length > 0) return exact;
	return all.filter((w) => w.title.toLowerCase().includes(t));
}

export function readChartData(input: ReadChartDataInput): ReadChartDataOutput {
	const all = widgetRegistry.list();
	if (all.length === 0) {
		return {
			found: false,
			warning: "not_found",
			message:
				"No widgets are currently registered on the dashboard. The user may need to scroll the dashboard into view, or the page hasn't finished rendering. Try refreshing page context.",
		};
	}

	let widget: RegisteredWidget | undefined;
	let warning: ReadChartDataOutput["warning"];

	if (input.chart_id) {
		widget = widgetRegistry.get(input.chart_id);
	}
	if (!widget && input.chart_title) {
		const matches = fuzzyTitleMatch(input.chart_title, all);
		if (matches.length === 1) {
			widget = matches[0];
		} else if (matches.length > 1) {
			widget = matches[0];
			warning = "ambiguous_title";
		}
	}
	if (!widget) {
		const sample = all.slice(0, 5).map((w) => `[${w.widgetId}] ${w.title}`);
		return {
			found: false,
			warning: "not_found",
			message: `Widget not found. Available widgets (sample): ${sample.join(
				" | "
			)}. Use one of these widgetIds.`,
		};
	}

	const include = input.include ?? ["series", "table", "filters", "source"];
	const maxRows = input.max_rows ?? DEFAULT_MAX_ROWS;
	const maxPoints = input.max_series_points ?? DEFAULT_MAX_SERIES_POINTS;

	const out: NonNullable<ReadChartDataOutput["widget"]> = {
		widgetId: widget.widgetId,
		title: widget.title,
		kind: widget.kind,
		chartLibrary: widget.chartLibrary,
		dashboardId: widget.dashboardId,
		framework: widget.framework,
		period: widget.period,
		units: widget.units,
		updatedAt: widget.updatedAt,
		registeredAt: widget.registeredAt,
		refWidgetId: widget.refWidgetId,
	};

	let truncated = false;

	if (include.includes("series") && widget.series) {
		out.series = widget.series.map((s) => {
			if (s.data.length > maxPoints) truncated = true;
			return { ...s, data: s.data.slice(0, maxPoints) };
		});
	}
	if (include.includes("table") && widget.table) {
		const rows = widget.table.rows;
		if (rows.length > maxRows) truncated = true;
		out.table = {
			headers: widget.table.headers,
			rows: rows.slice(0, maxRows),
			totalRows: widget.table.totalRows ?? rows.length,
		};
	}
	if (widget.totals) {
		out.totals = widget.totals;
	}
	if (widget.text) {
		out.text = widget.text;
	}
	if (widget.kpi) {
		out.kpi = widget.kpi;
	}
	if (widget.geo) {
		out.geo = widget.geo.slice(0, 200);
		if (widget.geo.length > 200) truncated = true;
	}
	if (include.includes("filters") && widget.filters) {
		out.filters = widget.filters;
	}
	if (include.includes("source") && widget.source) {
		out.source = widget.source;
	}

	let resolvedDrilldown: RegisteredWidget["series"] | undefined;
	if (include.includes("drilldown") && widget.drilldown) {
		out.drilldown = widget.drilldown;
		if (input.drilldown && widget.drilldown.linkedData) {
			const key = Object.values(input.drilldown).join("|");
			resolvedDrilldown = widget.drilldown.linkedData[key];
		}
	}

	return {
		found: true,
		widget: out,
		resolved_drilldown: resolvedDrilldown,
		warning: warning ?? (truncated ? "truncated" : undefined),
	};
}

/** Format the structured output into a Claude-friendly string. */
export function formatReadChartData(out: ReadChartDataOutput): string {
	if (!out.found) {
		return `### read_chart_data — not found\n${out.message ?? ""}`;
	}
	const w = out.widget!;
	const parts: string[] = [];
	parts.push(`### Widget: ${w.title} [${w.widgetId}]`);
	const meta: string[] = [];
	if (w.kind) meta.push(w.kind);
	if (w.chartLibrary) meta.push(w.chartLibrary);
	if (w.units) meta.push(`units=${w.units}`);
	if (w.period?.from) meta.push(`period=${w.period.from}${w.period.to ? `..${w.period.to}` : ""}`);
	if (w.framework?.id)
		meta.push(`framework=${w.framework.id}${w.framework.standard ? `/${w.framework.standard}` : ""}`);
	if (meta.length > 0) parts.push(meta.join(" · "));

	if (w.totals && Object.keys(w.totals).length > 0) {
		parts.push("\n**Totals**");
		for (const [k, v] of Object.entries(w.totals)) {
			parts.push(`- ${k}: ${v.toLocaleString()}`);
		}
	}

	if (w.text) {
		parts.push("\n**Text content**");
		parts.push(w.text);
	}

	if (w.kpi) {
		parts.push("\n**KPI**");
		if (w.kpi.caption) parts.push(`- Caption: ${w.kpi.caption}`);
		if (w.kpi.value !== undefined)
			parts.push(`- Value: ${w.kpi.value}${w.kpi.unit ? ` ${w.kpi.unit}` : ""}`);
		if (w.kpi.target !== undefined) parts.push(`- Target: ${w.kpi.target}`);
		if (w.kpi.previous !== undefined) parts.push(`- Previous: ${w.kpi.previous}`);
		if (w.kpi.change !== undefined)
			parts.push(`- Change: ${w.kpi.change.toFixed(1)}%`);
	}

	if (w.geo && w.geo.length > 0) {
		parts.push(`\n**Map markers (${w.geo.length})**`);
		const sample = w.geo.slice(0, 20);
		for (const m of sample) {
			const lbl = m.label ? `${m.label}: ` : "";
			const val = m.value != null ? ` (${m.value})` : "";
			parts.push(`- ${lbl}(${m.lat}, ${m.lng})${val}`);
		}
		if (w.geo.length > 20) parts.push(`…(+${w.geo.length - 20} more)`);
	}

	if (w.series && w.series.length > 0) {
		parts.push("\n**Series**");
		const renderScalar = (v: unknown): string => {
			if (v == null) return "";
			const t = typeof v;
			if (t === "string" || t === "number" || t === "boolean") return String(v);
			try {
				return JSON.stringify(v).slice(0, 80);
			} catch {
				return "[?]";
			}
		};
		for (const s of w.series) {
			const head = s.unit ? `${s.name} (${s.unit})` : s.name;
			const sample = s.data
				.slice(0, 30)
				.map((p) => `${renderScalar(p.x)}=${renderScalar(p.y)}`)
				.join(", ");
			parts.push(
				`- ${head}: ${sample}${s.data.length > 30 ? ` …(${s.data.length} pts)` : ""}`
			);
		}
	}

	if (w.table) {
		parts.push("\n**Table**");
		parts.push(w.table.headers.join(" | "));
		parts.push(w.table.headers.map(() => "---").join(" | "));
		const rowsToShow = w.table.rows.slice(0, 30);
		const renderCell = (c: unknown): string => {
			if (c == null) return "";
			const t = typeof c;
			if (t === "string" || t === "number" || t === "boolean") return String(c);
			try {
				return JSON.stringify(c).slice(0, 120);
			} catch {
				return "[unserializable]";
			}
		};
		for (const row of rowsToShow) {
			parts.push(row.map(renderCell).join(" | "));
		}
		if (w.table.rows.length > 30) {
			const totalRows = w.table.totalRows ?? w.table.rows.length;
			parts.push(`…(${totalRows} rows total)`);
		}
	}

	if (w.filters && w.filters.length > 0) {
		parts.push("\n**Active filters**");
		for (const f of w.filters) {
			parts.push(`- ${f.field} ${f.op} ${JSON.stringify(f.value)}`);
		}
	}

	if (w.source) {
		const bits: string[] = [];
		if (w.source.dataset) bits.push(`dataset=${w.source.dataset}`);
		if (w.source.lastUpdated)
			bits.push(`lastUpdated=${new Date(w.source.lastUpdated).toISOString()}`);
		if (bits.length > 0) parts.push(`\n**Source**: ${bits.join(" · ")}`);
	}

	if (out.warning) {
		parts.push(`\n_warning: ${out.warning}_`);
	}

	return parts.join("\n");
}
